import React, { useCallback, useMemo } from 'react';
import { Tooltip } from '@primer/react';

import {
  CheckCircleFillIcon,
  CheckIcon,
  XCircleFillIcon,
  XIcon,
} from '@primer/octicons-react';

import AlternatingRowDataTable from '../../../common/DataTable/AlternatingRowDataTable';
import Button from '../../../common/Button/Button';
import Icon from '../../../common/icons/Icon';
import { IconBadge } from '../../../common/Badge/Badge';
import Label from '../../../common/Labels/Label';

import ProjectRoadmapIcon from '../../../common/icons/ProjectRoadmapIcon';
import TrashFillIcon from '../../../common/icons/TrashFillIcon';

import { formatUserLastLoginDate } from '../../../../utils/date_utils';

const truncateText = (inputText, truncateAt = 8) => {
  if (inputText.length < truncateAt + 1) {
    return inputText;
  }
  return `${inputText.substring(0, truncateAt)}...`;
}

export const UserStatusBadge = ({ status, textColor = 'text-white' }) => (
  <IconBadge
    bgColor={status === 'Active' ? 'bg-green-500' : 'bg-gray-500'}
    icon={status === 'Active' ? CheckIcon : XIcon}
    textColor={textColor}
  >
    {status}
  </IconBadge>
)

const EmailCell = ({ value }) => (
  <Label>{truncateText(value, 36)}</Label>
)

const NameCell = ({ value }) => (
  <Label>{truncateText(value || '--', 20)}</Label>
)

const RoleCell = ({ value }) => {
  const isAdmin = value?.includes('admin');
  return (
    <Label>{isAdmin ? 'Admin' : 'User'}</Label>
  )
}

const StatusCell = ({ value }) => (
  <UserStatusBadge status={value ? 'Active' : 'Inactive'} />
)

const DateCell = ({ value }) => {
  if (value === null) {
    return <Label>--</Label>
  }
  return (
    <Label>{formatUserLastLoginDate(new Date(value).toISOString())}</Label>
  )
}

const ActionCellRenderer = ({
  row, meta = {},
}) => {
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  }

  const userId = row?.original?.id;
  const isActive = row?.original?.enabled;
  const FirstButtonIcon = isActive ? XCircleFillIcon : CheckCircleFillIcon;

  const user = row?.original;
  const onFirstButtonClick = useCallback((e) => {
    meta?.showUserActivationModal(user);
    e.stopPropagation()
  }, [meta, user])

  const onSecondButtonClick = useCallback((e) => {
    meta?.viewUserSessions(userId)
    e.stopPropagation()
  }, [meta, userId])

  const onThirdButtonClick = useCallback((e) => {
    meta?.removeUser(userId)
    e.stopPropagation()
  }, [meta, userId])

  return (
    <div className="flex flex-row justify-between items-center w-16">
      <Tooltip aria-label={isActive ? 'Deactivate user' : 'Activate user'}>
        <Button sx={buttonStyle} onClick={onFirstButtonClick} >
          <Icon
            icon={FirstButtonIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="8"
            width="16"
            height="16"
          />
        </Button>
      </Tooltip>
      <Tooltip aria-label="View active user sessions">
        <Button sx={buttonStyle} onClick={onSecondButtonClick} >
          <Icon
            icon={ProjectRoadmapIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="16"
            width="16"
            height="32"
          />
        </Button>
      </Tooltip>
      <Tooltip aria-label="Remove user">
        <Button sx={buttonStyle} onClick={onThirdButtonClick} >
          <Icon
            icon={TrashFillIcon}
            fill="text-brand"
            rectWidth="8"
            rectHeight="8"
            width="16"
            height="16"
          />
        </Button>
      </Tooltip>
    </div>
  )
}

export const UserAdminTable = ({
  users = [],
  selectedUserIds = [],
  setSelectedUserIds = () => {},
  showUserActivationModal = () => {},
  viewUserSessions = () => {},
  removeUser = () => {},
  onUserClick = () => {},
  hideCheckboxes = false,
  isSelectionColumnChecked = false,
  setSelectionColumnChecked = () => {},
  selectedPage = 1,
  resultsPerPage = 25,
}) => {
  const columns = useMemo(() => [
    {
      Header: 'Type',
      accessor: 'realmRoles',
      Cell: RoleCell,
      width: 24,
    },
    {
      Header: 'Status',
      accessor: 'enabled',
      Cell: StatusCell,
      width: 32,
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: EmailCell,
      width: 64,
    },
    {
      Header: 'First',
      accessor: 'firstName',
      Cell: NameCell,
      width: 40,
    },
    {
      Header: 'Last',
      accessor: 'lastName',
      Cell: NameCell,
      width: 40,
    },
    {
      Header: 'Last Login',
      accessor: 'lastLogin',
      Cell: DateCell,
      width: 48,
    },
    {
      Header: 'Actions',
      Cell: ActionCellRenderer,
      width: 40,
      disableSortBy: true,
    }
  ], []);

  const defaultSort = {
    id: 'email',
    desc: false
  }

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )
  const meta = React.useMemo(() => ({
    showUserActivationModal, viewUserSessions, removeUser,
  }), [removeUser, showUserActivationModal, viewUserSessions])

  return (
    <AlternatingRowDataTable
      id="user-admin-table"
      columns={columns}
      data={users}
      defaultColumn={defaultColumn}
      defaultSort={defaultSort}
      meta={meta}
      selectedRowIds={selectedUserIds}
      setSelectedRowIds={setSelectedUserIds}
      onItemClick={hideCheckboxes ? onUserClick : null}
      hideCheckboxes={hideCheckboxes}
      height={640}
      isSelectionColumnChecked={isSelectionColumnChecked}
      setSelectionColumnChecked={setSelectionColumnChecked}
      pagination={{
        resultsPerPage,
        selectedPage
      }}
      useSelectableRows
    />
  )
};

export default UserAdminTable;
